import { T, Num, useGT } from 'gt-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CopyIdButton } from 'src/components/common/CopyIdButton';
import type { GetTransferOrderResponse } from '@erp/rpc';

export const fmtTransferOrderDate = (iso: string | null | undefined): string =>
  iso ? iso.slice(0, 10) : '—';

type TransferOrderDetailsProps = {
  transferOrder: NonNullable<GetTransferOrderResponse['transferOrder']>;
  lineItems: GetTransferOrderResponse['lineItems'];
  charges: GetTransferOrderResponse['charges'];
  /** Show the TO number + status + copy-id row above the metadata grid. */
  showHeading?: boolean;
};

// Presentational transfer-order detail body (metadata + notes + line items +
// charges). Data is supplied by the caller, which owns the fetch and the scroll
// container. Used by both the standalone TO slide-in and the SO status tabs.
export function TransferOrderDetails({
  transferOrder,
  lineItems,
  charges,
  showHeading = false,
}: TransferOrderDetailsProps) {
  const gt = useGT();

  return (
    <div className="space-y-5">
      {showHeading && (
        <div className="flex items-center gap-2">
          <span className="font-medium">{transferOrder.transferNumber}</span>
          <CopyIdButton
            id={transferOrder.id}
            label={gt('Copy TO ID', { context: 'copy id button label' })}
          />
          <Badge variant="secondary">{transferOrder.status}</Badge>
        </div>
      )}

      {/* Header / metadata */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <Field label={gt('Source', { context: 'source warehouse' })}>
          {transferOrder.sourceWarehouse?.name ?? '—'}
        </Field>
        <Field label={gt('Destination', { context: 'destination warehouse' })}>
          {transferOrder.destinationWarehouse?.name ?? '—'}
        </Field>
        <Field label={gt('Order date', { context: 'order date' })}>
          {fmtTransferOrderDate(transferOrder.orderDate)}
        </Field>
        <Field label={gt('Ship date', { context: 'ship date' })}>
          {fmtTransferOrderDate(transferOrder.shipDate)}
        </Field>
        <Field label={gt('Delivery date', { context: 'delivery date' })}>
          {fmtTransferOrderDate(transferOrder.deliveryDate)}
        </Field>
        <Field label={gt('Quantity', { context: 'total quantity' })}>
          {transferOrder.quantity ?? '—'}
        </Field>
        <Field label={gt('Origin system', { context: 'origin system' })}>
          {transferOrder.originSystem ?? '—'}
        </Field>
        <Field label={gt('Origin ref', { context: 'origin id' })}>
          {transferOrder.originId ?? '—'}
        </Field>
      </dl>

      {transferOrder.notes && transferOrder.notes.length > 0 && (
        <div className="text-sm">
          <div className="mb-1 font-medium text-gray-700">
            <T context="notes section heading">Notes</T>
          </div>
          <ul className="list-inside list-disc text-muted-foreground">
            {transferOrder.notes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Line items */}
      <div>
        <div className="mb-2 text-sm font-medium text-gray-700">
          <T context="line items heading with count">
            Line Items (<Num>{lineItems.length}</Num>)
          </T>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <T context="line number column">#</T>
              </TableHead>
              <TableHead>
                <T context="sku column">SKU</T>
              </TableHead>
              <TableHead>
                <T context="product column">Product</T>
              </TableHead>
              <TableHead className="text-right">
                <T context="quantity column">Qty</T>
              </TableHead>
              <TableHead className="text-right">
                <T context="shipped quantity column">Shipped</T>
              </TableHead>
              <TableHead>
                <T context="unit of measure column">UoM</T>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineItems.map((li) => (
              <TableRow key={li.id}>
                <TableCell>{li.lineNumber ?? '—'}</TableCell>
                <TableCell>{li.productSku ?? '—'}</TableCell>
                <TableCell>{li.productName ?? '—'}</TableCell>
                <TableCell className="text-right">{li.quantity}</TableCell>
                <TableCell className="text-right">
                  {li.shippedQuantity ?? '—'}
                </TableCell>
                <TableCell>{li.unitOfMeasure ?? '—'}</TableCell>
              </TableRow>
            ))}
            {lineItems.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-4 text-center text-sm text-gray-500"
                >
                  <T context="no line items empty state">No line items.</T>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Charges */}
      {charges.length > 0 && (
        <div>
          <div className="mb-2 text-sm font-medium text-gray-700">
            <T context="charges section heading">Charges</T>
          </div>
          <ul className="space-y-1 text-sm">
            {charges.map((c) => (
              <li key={c.id} className="flex justify-between">
                <span>{c.categoryLabel ?? '—'}</span>
                <span>
                  {c.amount} {c.currencyCode}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="text-gray-800">{children}</dd>
    </div>
  );
}
